/**
 * FATE & GRÂCE — Finances API Service
 *
 * ── Achats (livraisons fournisseurs valorisées) ───────────
 *   GET  /api/achats/          ?fournisseur=&date_from=&date_to= → Achat[]
 *   POST /api/achats/          { fournisseur, reference?, note?, lignes:[{produit_id, quantite, prix_unitaire}] }
 *                              → Achat (EFFET IMMÉDIAT : stock mis à jour + dépense créée)
 *
 * ── Dépenses ──────────────────────────────────────────────
 *   GET    /api/depenses/            ?categorie_id=&date_from=&date_to=&search=
 *   POST   /api/depenses/            { libelle, montant, categorie_id, mode_paiement?, beneficiaire?, description?, date_depense? }
 *   PUT    /api/depenses/{id}/       (dépenses manuelles uniquement)
 *   DELETE /api/depenses/{id}/       (dépenses manuelles uniquement)
 *   GET    /api/depenses/resume/     ?period=30 → { recettes, depenses, resultat, par_categorie, evolution }
 *
 * ── Catégories de dépense ─────────────────────────────────
 *   GET    /api/depense-categories/
 *   POST   /api/depense-categories/  { nom, description? }
 *   DELETE /api/depense-categories/{id}/  (si non système et sans dépense)
 */

import { api, unwrap, downloadFile } from "./client";

const buildQs = (params = {}) =>
  new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v))
  ).toString();

/* ═══ ACHATS ════════════════════════════════════════════ */
export const achatsService = {
  async list(params = {}) {
    try {
      const qs = buildQs(params);
      const data = await api.get(`/achats/${qs ? "?" + qs : ""}`);
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [];
      throw err;
    }
  },

  async get(id) {
    return await api.get(`/achats/${id}/`);
  },

  /**
   * Enregistrer un achat — effet immédiat côté serveur :
   * stock mis à jour, mouvements ENTREE validés, dépense créée.
   * @param {{ fournisseur, reference?, note?, date_achat?, lignes:[{produit_id, quantite, prix_unitaire}] }} payload
   */
  async create(payload) {
    return await api.post("/achats/", payload);
  },

  /** Valider un achat en attente (manager / admin) → applique stock + dépense */
  async validate(id) {
    return await api.post(`/achats/${id}/validate/`);
  },

  /** Rejeter (supprimer) un achat en attente (manager / admin) */
  async reject(id, motif = "") {
    return await api.post(`/achats/${id}/reject/`, { motif });
  },
};

/* ═══ DÉPENSES ══════════════════════════════════════════ */
export const depensesService = {
  async list(params = {}) {
    try {
      const qs = buildQs(params);
      const data = await api.get(`/depenses/${qs ? "?" + qs : ""}`);
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [];
      throw err;
    }
  },

  /**
   * @param {{ libelle, montant, categorie_id, mode_paiement?, beneficiaire?, description?, date_depense? }} payload
   */
  async create(payload) {
    return await api.post("/depenses/", payload);
  },

  async update(id, payload) {
    return await api.put(`/depenses/${id}/`, payload);
  },

  async remove(id) {
    return await api.delete(`/depenses/${id}/`);
  },

  /**
   * Résumé financier : recettes vs dépenses sur une période.
   * @param {number|string|object} params
   *   - number/string : nombre de jours ou "month" (mois en cours)
   *   - object        : { period?, date_from?, date_to? }
   */
  async resume(params = "month") {
    try {
      const qs =
        typeof params === "object" && params !== null
          ? buildQs(params)
          : buildQs({ period: params });
      return await api.get(`/depenses/resume/${qs ? "?" + qs : ""}`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  /**
   * Export des dépenses de la période (Excel par défaut).
   * @param {object} params { format?, period?, date_from?, date_to?, categorie_id? }
   */
  async export({ format = "excel", ...period } = {}) {
    // 'fmt' et non 'format' (réservé par la négociation de contenu DRF → 404).
    const qs = buildQs({ fmt: format, ...period });
    const ext = format === "excel" ? "xlsx" : format;
    await downloadFile(`/depenses/export/?${qs}`, `depenses.${ext}`);
  },
};

/* ═══ CATÉGORIES DE DÉPENSE ═════════════════════════════ */
export const depenseCategoriesService = {
  async list() {
    try {
      const data = await api.get("/depense-categories/");
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [];
      throw err;
    }
  },

  async create(payload) {
    return await api.post("/depense-categories/", payload);
  },

  async remove(id) {
    return await api.delete(`/depense-categories/${id}/`);
  },
};
